import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { queryKeys } from "@/lib/query/keys";

/**
 * Expert registration step 2 — the email-confirmation gate. Parity with the old
 * app's `expert/email_confirmation.html` + `ExpertFlowMixin`: an expert whose
 * email isn't verified can't reach or submit the wizard. The account is created
 * and the confirmation email is sent at signup; the user must click the link in
 * that email. We re-check verification on demand and auto-poll every 10s (same
 * as the old app), refetching `/me`; once verified the parent renders the
 * wizard. There is no resend button here — the old app has none.
 */
export function EmailConfirmationGate() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState(false);
  const [showPending, setShowPending] = useState(false);

  const recheck = async (manual: boolean) => {
    if (manual) setChecking(true);
    try {
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
      if (manual) setShowPending(true);
    } finally {
      if (manual) setChecking(false);
    }
  };

  // Auto-poll every 10s so the wizard opens as soon as the link is clicked
  // (matches the old app's setInterval(checkStatus, 10000)).
  useEffect(() => {
    const id = setInterval(() => void recheck(false), 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldCheck className="size-5 text-primary" aria-hidden />
          {t("expert.emailConfirmation.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-foreground">
          {t("expert.emailConfirmation.body")}
        </p>
        <p className="text-xs text-muted-foreground">
          {t("expert.emailConfirmation.spamNote")}
        </p>
        <div className="space-y-2">
          <Button
            variant="outline"
            onClick={() => void recheck(true)}
            disabled={checking}
          >
            <RefreshCw
              className={checking ? "size-4 animate-spin" : "size-4"}
              aria-hidden
            />
            {checking
              ? t("expert.emailConfirmation.checking")
              : t("expert.emailConfirmation.check")}
          </Button>
          {showPending && !checking && (
            <p className="text-sm text-muted-foreground">
              {t("expert.emailConfirmation.pending")}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
